import '../init';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { createRouter, RouterProvider, type SearchParser, type SearchSerializer } from '@tanstack/react-router';
import { composeProviders, createProvider } from '@components/utils/composeProviders';
import { routeTree } from './routes.gen';
import { AppRecordingLayer } from 'vl-common/src/logging/AppRecordingLayer';
import { useShouldRecord } from 'vl-common/src/logging/useShouldRecord';
import { generateA11ySnapshot } from 'vl-common/src/a11ySnapshotHelper';

if (window.Cypress) {
  window.generateA11ySnapshot = generateA11ySnapshot;
}

const container = document.getElementById('root') as HTMLElement;

const root = createRoot(container);

const parseSearch: SearchParser = (search) => {
  const searchParams = new URLSearchParams(search);
  return Object.fromEntries(searchParams.entries());
};

const stringifySearch: SearchSerializer = (query) => {
  // Filter out undefined/null values if necessary, then stringify
  const cleanQuery = Object.fromEntries(Object.entries(query).filter(([_, v]) => v !== undefined && v !== null));

  const searchParams = new URLSearchParams(cleanQuery as Record<string, string>).toString();
  return searchParams ? `?${searchParams}` : '';
};

console.log('BASE_URL', import.meta.env.BASE_URL);

if (window.location.pathname.endsWith('/index.html')) {
  const cleanPath = window.location.pathname.replace(/index\.html$/, '');
  window.history.replaceState(window.history.state, '', cleanPath + window.location.search + window.location.hash);
}

const router = createRouter({
  routeTree,
  basepath: import.meta.env.BASE_URL,
  parseSearch,
  stringifySearch,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const RecordingSetup = () => {
  const shouldRecord = useShouldRecord(import.meta.env.DEV && import.meta.env.REACT_APP_ENABLE_CY_CAPTURE === 'true');

  return shouldRecord ? <AppRecordingLayer /> : null;
};

const Providers = composeProviders([createProvider(HelmetProvider), createProvider(RouterProvider, { router })]);

root.render(
  <React.StrictMode>
    <RecordingSetup />
    <Providers />
  </React.StrictMode>
);
